import { useSearchParams } from 'react-router-dom'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { useBillingsForecast } from '@/hooks/use-api'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useOverflowFocus } from '@/hooks/use-overflow-focus'
import { explainError } from '@/lib/explain-error'
import { formatDate } from '@/lib/utils'
import { billingsFiscalMonths, billingsFiscalYearOf, currentMonthKey } from '@/lib/billings-fiscal'
import type { BillingsForecastTotals } from '@/types'

/**
 * The table Jason runs the Monday sales meeting from.
 *
 * His words: "On the weekly sales meetings, I go over 'last weeks forecast' vs. 'This
 * weeks forecast' and the invoiced sales." This reproduces the spreadsheet he was doing
 * that in - rep rows, then Subtotal, Unassigned, Total, the month's target, and each
 * column as a percentage of it.
 *
 *
 * THREE COLUMNS, NEVER SUMMED
 *
 * Forecast and invoiced are the same revenue measured two ways: an opportunity that
 * closes becomes an invoice. Adding a forecast figure to an invoiced one counts that
 * revenue twice, which is why Sales Trackers was built on QuickBooks actuals with a
 * standing rule that pipeline never joins them. Jason's own sheet already respects it -
 * three columns side by side, and only the percentage row divides by target. Nothing on
 * this page adds across a row, and nothing should be added later.
 *
 *
 * WHERE THE FORECAST COMES FROM
 *
 * The CRM's weighted pipeline, captured every Monday morning, per rep per month. Jason
 * settled this: "9/10 times it is the CRM's weighted pipeline... however they SHOULD be
 * in the CRM, so I'd go with CRM numbers only. It will force me to stop being unique."
 * So there is no manual entry and no override. A deal missing from the table is a deal
 * missing from the CRM, and that is the intended feedback.
 *
 * It has to be captured going forward and cannot be reconstructed. The history trigger
 * records amount, stage, probability and close date, but record creation and owner
 * changes stopped being tracked in May 2026 when Salesforce was decommissioned - so a
 * rebuilt past week would not know which opportunities existed then, nor who owned them,
 * which are the two facts a per-rep historical forecast rests on. Hence: no backfill, and
 * the first week shows no comparison.
 *
 *
 * WHAT A BLANK MEANS
 *
 * "Not captured" is not zero. On the first week there is no previous capture, and a 0 in
 * that column would read as "the forecast was zero" on exactly the week this page is
 * first opened. The API returns null rather than 0 for the same reason, and the two are
 * rendered differently here throughout.
 *
 * The Unassigned row is everything that is not an active named rep, on BOTH measures.
 * It was first written as invoiced-only, on the reasoning that every opportunity has an
 * owner so no forecast could fall outside the named reps. The premise holds and the
 * conclusion does not: an owner need not be an active rep, and the forecast captured for
 * anyone off the roster was then in no row and no total. On 28 August that quietly cost
 * March $1,001 and June $250. Whatever does not match an active rep now lands here, so the
 * Total is always the whole - which the API's contract test asserts against the raw
 * capture.
 *
 *
 * ACCESSIBILITY
 *
 * Movement between the two forecast columns is spelled out in words ("up" / "down") as
 * well as signed, never carried by colour, which is the same rule TargetVsActual and
 * BillingsMatrix follow. Summary rows are <th scope="row"> inside a <tfoot> so a screen
 * reader announces which figure it is reading, the table has a real caption, and the
 * percentage row is labelled with the target it divides by rather than being a bare row
 * of numbers.
 */

const money = (n: number) => n.toLocaleString('en-CA', { maximumFractionDigits: 0 })

/** A figure, or an explicit statement that there is not one. The distinction matters. */
function Figure({ value, absent = 'Not captured' }: { value: number | null; absent?: string }) {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground text-xs">{absent}</span>
  }
  return <span className="tabular-nums">{money(value)}</span>
}

/**
 * Percentage of target, or null when there is no target to divide by.
 *
 * Always one decimal place. Rounding to a whole number where it happens to land there
 * put "51%" next to "50.7%" in the same row, which reads as two different measures
 * rather than one measured twice.
 */
function percentOf(value: number | null, target: number | null) {
  if (value === null || target === null || target === 0) return null
  return ((value / target) * 100).toFixed(1)
}

/*
 * Dates are formatted through Intl on a Date built from calendar COMPONENTS, never by
 * parsing a string. `new Date('2026-08-24')` is parsed as UTC midnight and then displayed
 * in local time, which renders the 23rd anywhere west of Greenwich - the same class of
 * off-by-one that made the capture service read the wrong day. The repo lints against
 * `.toLocaleDateString()` for exactly this reason, and `formatDate` handles it correctly
 * for date-only strings.
 */
const MONTH_YEAR = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' })

function monthLabel(monthKey: string) {
  const [y, m] = monthKey.split('-').map(Number)
  return MONTH_YEAR.format(new Date(y as number, (m as number) - 1, 1))
}

/** A capture week, shown as the Monday it belongs to. */
const weekLabel = (iso: string) => formatDate(iso)

export default function SalesTrackersWeeklyForecast() {
  useDocumentTitle('Weekly forecast')

  /*
   * Month and week live in the URL so the view is shareable and the back button steps
   * between weeks. Jason opens this in a meeting; "here is the week we looked at" needs
   * to be a link he can paste, not a sequence of clicks to repeat.
   */
  const [params, setParams] = useSearchParams()
  const month = params.get('month') ?? currentMonthKey()
  const week = params.get('week') ?? undefined

  const set = (key: string, value?: string) => {
    const p = new URLSearchParams(params)
    if (value) p.set(key, value)
    else p.delete(key)
    // Changing month invalidates the chosen week: they are weeks OF that month's
    // captures, and carrying one across would ask for a week that does not exist there.
    if (key === 'month') p.delete('week')
    setParams(p, { replace: true })
  }

  const { data, isLoading, error } = useBillingsForecast(month, week)

  // Called unconditionally, though the table it measures renders inside `{data && ...}`.
  // The hook takes a CALLBACK ref for exactly that reason: the element arrives late, and
  // an effect that looked once on mount would find nothing and never look again. The
  // label names the content, because "Scrollable table" names only the mechanism.
  const overflow = useOverflowFocus(`Weekly forecast for ${monthLabel(month)}, by rep`)

  const fy = billingsFiscalYearOf(month)
  const target = data?.target ?? null

  const columns: Array<{ key: keyof BillingsForecastTotals; label: string; hint?: string }> = [
    { key: 'last_forecast', label: 'Last week', hint: data?.last_capture ?? undefined },
    { key: 'this_forecast', label: 'This week', hint: data?.this_capture ?? undefined },
    { key: 'invoiced', label: 'Invoiced' },
  ]

  return (
    <section className="p-6 space-y-6" aria-labelledby="sales-trackers-heading">
      <div>
        <h1 id="sales-trackers-heading" className="text-2xl font-semibold">
          Weekly forecast
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Last week&rsquo;s forecast against this week&rsquo;s, and what has actually been invoiced.
          Forecast is the CRM&rsquo;s weighted pipeline; invoiced is QuickBooks. Amounts are CAD.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <Label htmlFor="forecast-month">Month</Label>
          <select
            id="forecast-month"
            className="mt-1 block h-9 min-w-[12rem] rounded-md border border-input bg-background px-3 text-sm"
            value={month}
            onChange={(e) => set('month', e.target.value)}
          >
            {/* The month's own fiscal year, plus the one before, so a March meeting can
                still look back at February without changing two controls. */}
            {[fy - 1, fy]
              .flatMap((y) => billingsFiscalMonths(y))
              .map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
          </select>
        </div>

        {data && data.available_weeks.length > 1 && (
          <div>
            <Label htmlFor="forecast-week">Week</Label>
            <select
              id="forecast-week"
              className="mt-1 block h-9 min-w-[12rem] rounded-md border border-input bg-background px-3 text-sm"
              value={week ?? ''}
              onChange={(e) => set('week', e.target.value || undefined)}
            >
              <option value="">Latest ({weekLabel(data.available_weeks[0] as string)})</option>
              {data.available_weeks.map((w) => (
                <option key={w} value={w}>
                  Week of {weekLabel(w)}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Reopens the figures as they stood that Monday.
            </p>
          </div>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Could not load the weekly forecast</AlertTitle>
          <AlertDescription>{explainError(error)}</AlertDescription>
        </Alert>
      )}

      {isLoading && <p className="text-sm text-muted-foreground">Loading the forecast…</p>}

      {data && !data.this_capture && (
        <Alert>
          <AlertTitle>No forecast has been captured for {monthLabel(month)} yet</AlertTitle>
          <AlertDescription>
            The forecast is captured automatically every Monday morning and cannot be worked out
            after the fact, so this table starts filling in from the first capture onwards. Invoiced
            figures below are complete and are not affected.
          </AlertDescription>
        </Alert>
      )}

      {data && (
        <Card>
          <CardHeader>
            <CardTitle>{monthLabel(data.month_key)}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {data.this_capture
                ? `Forecast captured ${weekLabel(data.this_capture)}`
                : 'No forecast captured'}
              {data.last_capture
                ? `, compared with ${weekLabel(data.last_capture)}.`
                : data.this_capture
                  ? '. There is no earlier capture to compare with yet.'
                  : '.'}
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto" ref={overflow.ref} {...overflow.regionProps}>
              <table className="w-full text-sm">
                <caption className="sr-only">
                  Weekly forecast and invoiced billings for {monthLabel(data.month_key)}, by rep,
                  with company totals against the monthly target
                </caption>
                <thead>
                  <tr className="border-b">
                    <th scope="col" className="text-left font-medium py-2 pr-4">
                      Rep
                    </th>
                    {columns.map((c) => (
                      <th key={c.key} scope="col" className="text-right font-medium py-2 pr-4">
                        {c.label}
                        {c.hint && (
                          <span className="block text-xs font-normal text-muted-foreground">
                            {weekLabel(c.hint)}
                          </span>
                        )}
                      </th>
                    ))}
                    <th scope="col" className="text-right font-medium py-2">
                      Movement
                      <span className="block text-xs font-normal text-muted-foreground">
                        week on week
                      </span>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {data.rows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-4 text-muted-foreground">
                        No rep has a forecast or any billings in this month.
                      </td>
                    </tr>
                  )}
                  {data.rows.map((r) => {
                    const moved =
                      r.this_forecast !== null && r.last_forecast !== null
                        ? r.this_forecast - r.last_forecast
                        : null
                    return (
                      <tr key={r.rep_user_id} className="border-b last:border-0">
                        <th scope="row" className="text-left font-normal py-2 pr-4">
                          {r.display}
                        </th>
                        <td className="py-2 pr-4 text-right">
                          <Figure value={r.last_forecast} />
                        </td>
                        <td className="py-2 pr-4 text-right">
                          <Figure value={r.this_forecast} />
                        </td>
                        <td className="py-2 pr-4 text-right tabular-nums">{money(r.invoiced)}</td>
                        {/* The word carries the direction; colour only reinforces it. */}
                        <td
                          className={`py-2 text-right tabular-nums ${
                            moved !== null && moved < 0 ? 'text-destructive' : ''
                          }`}
                        >
                          {moved === null ? (
                            <span className="text-muted-foreground text-xs">—</span>
                          ) : moved === 0 ? (
                            <span className="text-muted-foreground text-xs">no change</span>
                          ) : (
                            <>
                              {moved > 0 ? '+' : '−'}
                              {money(Math.abs(moved))}{' '}
                              <span className="text-xs">{moved > 0 ? 'up' : 'down'}</span>
                            </>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>

                <tfoot>
                  <tr className="border-t-2 font-medium">
                    <th scope="row" className="text-left py-2 pr-4">
                      Subtotal
                      <span className="block text-xs font-normal text-muted-foreground">
                        named reps
                      </span>
                    </th>
                    <td className="py-2 pr-4 text-right">
                      <Figure value={data.subtotal.last_forecast} />
                    </td>
                    <td className="py-2 pr-4 text-right">
                      <Figure value={data.subtotal.this_forecast} />
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {money(data.subtotal.invoiced)}
                    </td>
                    <td className="py-2" />
                  </tr>

                  <tr className="border-b">
                    <th scope="row" className="text-left font-normal py-2 pr-4">
                      Unassigned
                      <span className="block text-xs text-muted-foreground">
                        anything not attributed to an active rep
                      </span>
                    </th>
                    {/* This row can hold a forecast, contrary to what an earlier version
                        of this file asserted. "Every opportunity has an owner" is true;
                        "so every forecast belongs to a named rep" does not follow, because
                        an owner need not be an active rep. Whatever falls outside the
                        roster lands here rather than disappearing from the total. */}
                    <td className="py-2 pr-4 text-right">
                      <Figure value={data.unassigned.last_forecast} />
                    </td>
                    <td className="py-2 pr-4 text-right">
                      <Figure value={data.unassigned.this_forecast} />
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {money(data.unassigned.invoiced)}
                    </td>
                    <td className="py-2" />
                  </tr>

                  <tr className="border-b font-semibold">
                    <th scope="row" className="text-left py-2 pr-4">
                      Total
                    </th>
                    <td className="py-2 pr-4 text-right">
                      <Figure value={data.total.last_forecast} />
                    </td>
                    <td className="py-2 pr-4 text-right">
                      <Figure value={data.total.this_forecast} />
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {money(data.total.invoiced)}
                    </td>
                    <td className="py-2" />
                  </tr>

                  <tr className="border-b">
                    <th scope="row" className="text-left font-normal py-2 pr-4">
                      Target
                    </th>
                    {/* Centred across all three columns on purpose. Right-aligned it
                        landed under "Invoiced" and read as that column's target, when it
                        is the one figure all three are measured against. */}
                    <td colSpan={3} className="py-2 pr-4 text-center tabular-nums">
                      {target === null ? (
                        <span className="text-muted-foreground text-xs">
                          No company target set for this month
                        </span>
                      ) : (
                        <>
                          {money(target)}
                          <span className="text-xs text-muted-foreground"> for the month</span>
                        </>
                      )}
                    </td>
                    <td className="py-2" />
                  </tr>

                  <tr>
                    <th scope="row" className="text-left font-normal py-2 pr-4">
                      Of target
                    </th>
                    {(['last_forecast', 'this_forecast', 'invoiced'] as const).map((k) => {
                      const pct = percentOf(data.total[k], target)
                      return (
                        <td key={k} className="py-2 pr-4 text-right tabular-nums">
                          {pct === null ? (
                            <span className="text-muted-foreground text-xs">—</span>
                          ) : (
                            `${pct}%`
                          )}
                        </td>
                      )
                    })}
                    <td className="py-2" />
                  </tr>
                </tfoot>
              </table>
            </div>

            <p className="text-xs text-muted-foreground mt-4">
              Forecast and invoiced are the same revenue measured two ways, so they are never added
              together. Forecast is the weighted pipeline: each open deal at its probability, plus
              won deals in full.
            </p>
          </CardContent>
        </Card>
      )}
    </section>
  )
}
